import { FC, useContext } from "react";
import { Controller, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import {
  AsyncSelect,
  Form,
  FormField,
  ModalDialog,
  Numeric,
  Switch,
  Textarea,
  TranslatableField,
  type FormChildrenProps,
} from "@rothoblaas/design-system";
import { translatableFieldProps } from "../../../../utils/translatableField";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ConnectorFamily, UpdateConnectorFamilyBody } from "../../../../http/deckDesigner/types/connectorFamilies";
import { ScrewFamily } from "../../../../http/deckDesigner/types/screwFamilies";
import useDeckConnectorFamiliesApi from "../../../../http/deckDesigner/useConnectorFamilies";
import useDeckScrewFamiliesApi from "../../../../http/deckDesigner/useScrewFamilies";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";

interface ConnectorFamilyFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  groovingMinHeight: number | null;
  groovingMaxHeight: number | null;
  groovingThickness: number | null;
  isScrew: boolean;
  requiresBoardScrew: boolean;
  maxBoardThickness: number | null;
  screwFamilies: ScrewFamily[];
}

interface ConnectorFamilyFormProps {
  connectorFamily: ConnectorFamily;
  onClose: () => void;
}

type MillimetersFieldName = "groovingMinHeight" | "groovingMaxHeight" | "groovingThickness" | "maxBoardThickness";

const ConnectorFamilyFields: FC<{ form: FormChildrenProps<ConnectorFamilyFormBody> }> = ({ form }) => {
  const { control, register, setValue } = form;
  const i18n = useI18n();
  const screwFamiliesApi = useDeckScrewFamiliesApi();
  const screwFamilyOptions = useAsyncOptions(screwFamiliesApi.getDeckScrewFamilies);
  const values = useWatch({ control }) as ConnectorFamilyFormBody;
  // Screw connector families take no screw families: the switch and the select exclude each other.
  const isScrew = values.isScrew;
  const hasScrewFamilies = values.screwFamilies.length > 0;

  const millimetersField = (name: MillimetersFieldName, label: string) => (
    <Controller
      name={name}
      control={control}
      rules={{ min: 0 }}
      render={({ field, fieldState }) => (
        <FormField label={label} error={fieldState.error} isOptional>
          {({ labelId, isInvalid }) => (
            <Numeric
              id={labelId}
              value={field.value ?? undefined}
              min={0}
              decimalScale={0}
              suffix={" " + i18n.translation.common.unitMm}
              isInvalid={isInvalid}
              onValueChange={(value) => field.onChange(value)}
            />
          )}
        </FormField>
      )}
    />
  );

  return (
    <FormFieldsContainer fullWidth>
      <TranslatableTextField form={form} />
      <FormField
        label={i18n.translation.deckDesignerModule.connectorFamilies.description}
        error={form.formState.errors.description}
        isOptional
      >
        {({ labelId, isInvalid }) => (
          <TranslatableField
            {...translatableFieldProps("description", values, setValue, i18n.translation)}
            dialogTitle={i18n.translation.deckDesignerModule.connectorFamilies.description}
            type="textarea"
          >
            <Textarea id={labelId} isInvalid={isInvalid} {...register("description")} />
          </TranslatableField>
        )}
      </FormField>
      {millimetersField("groovingMinHeight", i18n.translation.common.groovingMinHeight)}
      {millimetersField("groovingMaxHeight", i18n.translation.common.groovingMaxHeight)}
      {millimetersField("groovingThickness", i18n.translation.common.groovingThickness)}
      {millimetersField("maxBoardThickness", i18n.translation.deckDesignerModule.connectorFamilies.maxBoardThickness)}
      <Controller
        name="screwFamilies"
        control={control}
        render={({ field, fieldState }) => (
          <FormField label={i18n.translation.deckDesignerModule.screwFamilies.plural} error={fieldState.error}>
            {({ labelId }) => (
              <AsyncSelect<ScrewFamily, true>
                {...screwFamilyOptions}
                inputId={labelId}
                value={field.value}
                isMulti
                isDisabled={isScrew}
                placeholder={`${i18n.translation.common.select}...`}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                isInvalid={fieldState.invalid}
                onChange={(options) => field.onChange(Array.from(options))}
              />
            )}
          </FormField>
        )}
      />
      <Controller
        name="isScrew"
        control={control}
        render={({ field }) => (
          <FormField label={i18n.translation.deckDesignerModule.connectorFamilies.isScrew}>
            {({ labelId }) => (
              <Switch
                id={labelId}
                checked={field.value}
                disabled={!field.value && hasScrewFamilies}
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          </FormField>
        )}
      />
      <Controller
        name="requiresBoardScrew"
        control={control}
        render={({ field }) => (
          <FormField label={i18n.translation.deckDesignerModule.connectorFamilies.requiresBoardScrew}>
            {({ labelId }) => (
              <Switch id={labelId} checked={field.value} onChange={(checked) => field.onChange(checked)} />
            )}
          </FormField>
        )}
      />
    </FormFieldsContainer>
  );
};

const ConnectorFamilyForm: FC<ConnectorFamilyFormProps> = ({ connectorFamily, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const connectorFamiliesApi = useDeckConnectorFamiliesApi();

  const defaultValues: ConnectorFamilyFormBody = {
    name: connectorFamily.name ?? null,
    nameIt: connectorFamily.nameIt ?? null,
    nameDe: connectorFamily.nameDe ?? null,
    nameEs: connectorFamily.nameEs ?? null,
    nameFr: connectorFamily.nameFr ?? null,
    description: connectorFamily.description ?? null,
    descriptionIt: connectorFamily.descriptionIt ?? null,
    descriptionDe: connectorFamily.descriptionDe ?? null,
    descriptionEs: connectorFamily.descriptionEs ?? null,
    descriptionFr: connectorFamily.descriptionFr ?? null,
    groovingMinHeight: connectorFamily.groovingMinHeight ?? null,
    groovingMaxHeight: connectorFamily.groovingMaxHeight ?? null,
    groovingThickness: connectorFamily.groovingThickness ?? null,
    isScrew: connectorFamily.isScrew ?? false,
    requiresBoardScrew: connectorFamily.requiresBoardScrew ?? false,
    maxBoardThickness: connectorFamily.maxBoardThickness ?? null,
    screwFamilies: connectorFamily.isScrew ? [] : (connectorFamily.screwFamilies ?? []),
  };

  const onSubmit = () => async (formData: ConnectorFamilyFormBody) => {
    const body: UpdateConnectorFamilyBody = {
      name: formData.name ?? "",
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameEs: formData.nameEs || null,
      nameFr: formData.nameFr || null,
      description: formData.description?.trim() || null,
      descriptionIt: formData.descriptionIt?.trim() || null,
      descriptionDe: formData.descriptionDe?.trim() || null,
      descriptionEs: formData.descriptionEs?.trim() || null,
      descriptionFr: formData.descriptionFr?.trim() || null,
      groovingMinHeight: formData.groovingMinHeight,
      groovingMaxHeight: formData.groovingMaxHeight,
      groovingThickness: formData.groovingThickness,
      isScrew: formData.isScrew,
      requiresBoardScrew: formData.requiresBoardScrew,
      maxBoardThickness: formData.maxBoardThickness,
      screwFamilies: formData.isScrew ? [] : formData.screwFamilies,
    };

    try {
      await connectorFamiliesApi.updateDeckConnectorFamily(connectorFamily.code, body);
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.connectorFamilies.singular}`}
    >
      <Form<ConnectorFamilyFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => <ConnectorFamilyFields form={form} />}
      </Form>
    </ModalDialog>
  );
};

export default ConnectorFamilyForm;
